import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle, AlertTriangle, HelpCircle, BookOpen, ChevronRight } from "lucide-react";
import { RatgeberContent, getRatgeberBySlug } from "@/data/ratgeberData";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import StickyHeader from "@/components/StickyHeader";
import CookieConsent from "@/components/CookieConsent";

interface RatgeberTemplateProps {
  content: RatgeberContent;
}

const RatgeberTemplate = ({ content }: RatgeberTemplateProps) => {
  const pageUrl = `https://www.love-the-nature.de/ratgeber/${content.slug}`;
  const ogImage = "https://www.love-the-nature.de/og-image.png";

  const schemaFAQ = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": content.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const schemaArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": content.h1,
    "description": content.metaDescription,
    "image": ogImage,
    "url": pageUrl,
    "datePublished": "2025-12-01",
    "dateModified": "2025-12-03",
    "author": {
      "@type": "Organization",
      "name": "Love The Nature"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Love The Nature",
      "url": "https://www.love-the-nature.de",
      "logo": {
        "@type": "ImageObject",
        "url": ogImage
      }
    }
  };

  const schemaBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Startseite",
        "item": "https://www.love-the-nature.de"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Ratgeber",
        "item": "https://www.love-the-nature.de/ratgeber"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": content.title
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{content.metaTitle}</title>
        <meta name="description" content={content.metaDescription} />
        <meta property="og:title" content={content.metaTitle} />
        <meta property="og:description" content={content.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:locale" content="de_DE" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={content.metaTitle} />
        <meta name="twitter:description" content={content.metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">{JSON.stringify(schemaFAQ)}</script>
        <script type="application/ld+json">{JSON.stringify(schemaArticle)}</script>
        <script type="application/ld+json">{JSON.stringify(schemaBreadcrumb)}</script>
      </Helmet>

      <StickyHeader />
      <article className="min-h-screen bg-background">
        {/* Breadcrumb */}
        <nav className="bg-muted/30 border-b border-border/50">
          <div className="container mx-auto px-4 py-3">
            <ol className="flex items-center gap-2 text-sm text-muted-foreground">
              <li><Link to="/" className="hover:text-primary transition-colors">Startseite</Link></li>
              <ChevronRight className="w-4 h-4" />
              <li><Link to="/ratgeber" className="hover:text-primary transition-colors">Ratgeber</Link></li>
              <ChevronRight className="w-4 h-4" />
              <li className="text-foreground font-medium">{content.title}</li>
            </ol>
          </div>
        </nav>

        {/* Hero Section */}
        <header className="bg-gradient-to-b from-primary/5 to-background py-12 md:py-16">
          <div className="container mx-auto px-4">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Startseite
            </Link>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-6">
              {content.h1}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl">
              {content.intro}
            </p>
          </div>
        </header>

        <main className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            
            {/* Ursachen Section */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
                <HelpCircle className="w-6 h-6 text-primary" />
                Mögliche Ursachen
              </h2>
              <ul className="space-y-3">
                {content.causes.map((cause, index) => (
                  <li key={index} className="flex items-start gap-3 text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                    {cause}
                  </li>
                ))}
              </ul>
            </section>

            {/* Naturmittel Section */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-primary" />
                Bewährte Naturmittel
              </h2>
              <div className="grid gap-4">
                {content.remedies.map((remedy, index) => (
                  <div 
                    key={index} 
                    className="bg-card border border-border rounded-xl p-5 hover:shadow-lg transition-shadow"
                  >
                    <h3 className="text-lg font-semibold text-foreground mb-2">{remedy.name}</h3>
                    <p className="text-primary font-medium mb-2">{remedy.benefit}</p>
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium">Anwendung:</span> {remedy.application}
                    </p>
                  </div>
                ))}
              </div>
              
              {/* Teaser Box */}
              <div className="mt-6 bg-primary/10 border border-primary/20 rounded-xl p-6">
                <p className="text-foreground mb-4">
                  <strong>💡 Tipp:</strong> Im E-Book "Naturkraft" findest du alle Informationen zu Dosierung, 
                  Wechselwirkungen und Bezugsquellen – für über 100 Beschwerden.
                </p>
                <Button asChild className="w-full sm:w-auto">
                  <a href="https://payhip.com/b/NWTP2" target="_blank" rel="noopener noreferrer">
                    <BookOpen className="w-4 h-4 mr-2" />
                    E-Book für 19,99€ sichern
                  </a>
                </Button>
              </div>
            </section>

            {/* Tipps Section */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-6">
                Zusätzliche Tipps
              </h2>
              <ul className="space-y-3">
                {content.tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-3 text-muted-foreground">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </section>

            {/* Wann zum Arzt Section */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
                Wann zum Arzt?
              </h2>
              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-5">
                <ul className="space-y-2">
                  {content.whenToSeeDoctor.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-amber-800 dark:text-amber-200">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* FAQ Section */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-6">
                Häufig gestellte Fragen
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {content.faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`faq-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>

            {/* Related Topics */}
            {content.relatedTopics.length > 0 && (
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-foreground mb-6">
                  Verwandte Themen
                </h2>
                <div className="flex flex-wrap gap-3">
                  {content.relatedTopics.map((topic, index) => {
                    const relatedContent = getRatgeberBySlug(topic);
                    if (relatedContent) {
                      return (
                        <Link
                          key={index}
                          to={`/ratgeber/${topic}`}
                          className="px-4 py-2 bg-muted hover:bg-primary/10 border border-border rounded-full text-sm font-medium transition-colors"
                        >
                          {relatedContent.title}
                        </Link>
                      );
                    }
                    return null;
                  })}
                </div>
              </section>
            )}

            {/* Final CTA */}
            <section className="bg-gradient-to-br from-primary/10 via-background to-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold text-foreground mb-4">
                Alle Naturmittel auf einen Blick
              </h2>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Das E-Book "Naturkraft" enthält 150+ Naturmittel für über 200 Beschwerden – 
                mit genauen Dosierungen, Warnhinweisen und Bezugsquellen.
              </p>
              <Button size="lg" asChild>
                <a href="https://payhip.com/b/NWTP2" target="_blank" rel="noopener noreferrer">
                  Jetzt E-Book sichern – 19,99€
                </a>
              </Button>
            </section>
          </div>
        </main>
      </article>
      <CookieConsent />
    </>
  );
};

export default RatgeberTemplate;
